import React, { useCallback, useEffect, useState } from "react";
import { printingApi } from "../../lib/printing/printingApi";
import { errorCode, formatWhen, operatorMessage, ROLE_LABELS, TRIGGER_LABELS } from "../../lib/printing/printingModel";
import { SectionCard, Notice, Pill, Modal, btnPrimary, btnSecondary, btnDanger } from "./PrintingUi";

/**
 * Oct 2026 — the operator control for automatic packing slips.
 *
 * A packing slip is a separate document from the customer receipt: it has
 * its own settings (Defaults and catalog overrides tab) and its own on/off
 * switch here. When both this business's activation and the server master
 * switch are on, every paid order queues ONE packing slip, independently
 * of the receipt, at the moment the order is admitted for fulfilment:
 * right away for Dine-In, Takeout, Pickup and Parcel Shipping; after
 * acceptance (and, for Uber Direct, a successful courier request) for
 * delivery; when a scheduled order is activated for the kitchen.
 *
 * This control is the only place that turns on/off the two packing-slip
 * rules it manages (cash received, card payment — mutually exclusive, so
 * one slip per order). Enable stamps the activation with the server clock,
 * so no order paid earlier can ever print; enabling again while active
 * only repairs the managed rules and keeps that time. Disable turns both
 * managed rules off and clears the activation for every catalog.
 */
// Operator wording for every automatic packing-slip state that is not a
// queued job (Oct 2026 Product Owner decision: all of them are visible).
export const SLIP_REASON_LABELS = {
  awaiting_fulfillment_admission: "Waiting for the order to be accepted",
  awaiting_delivery_admission: "Waiting for delivery acceptance / courier",
  awaiting_reconciliation: "Waiting: payment held for review",
  awaiting_scheduled_activation: "Waiting for the scheduled time",
  awaiting_scheduled_activation_invalid_time: "Needs attention: scheduled time missing or invalid",
  order_terminal: "Not printed: order cancelled, rejected or failed",
  order_already_completed: "Not printed: order already completed",
  payment_refunded: "Not printed: payment refunded",
  payment_no_longer_active: "Not printed: payment refunded",
  admission_window_expired: "Expired: not accepted within 24 hours",
  scheduled_window_expired: "Expired: not activated within 24 hours of its scheduled time",
  scheduled_time_invalid: "Expired: scheduled time missing or invalid",
  printer_wait_expired: "Expired: no printer within 24 hours",
  stale_event: "Expired: not printed within 24 hours of becoming ready",
  processing_error_retrying: "Retrying after a temporary error (up to 24 hours)",
  processing_error_expired: "Needs attention: kept failing for 24 hours — print by hand if needed",
  eligibility_time_missing: "Needs attention: order ready but its acceptance time is missing — print by hand",
  transmission_outcome_unknown: "Needs review: sent to the printer, but the printer agent stopped responding — check before printing by hand",
  failed_after_authorization: "Needs review: the printer agent could not print it — print by hand",
  dismissed_by_operator: "Dismissed by staff",
};

export function slipReasonLabel(reason) {
  if (!reason) return "";
  if (reason.startsWith("awaiting_printer:")) return "Waiting for a packing printer (up to 24 hours)";
  return SLIP_REASON_LABELS[reason] || reason;
}

export default function AutomaticPackingSlipsPanel({ status, isAdmin, onChanged, notify, onGoTo }) {
  const [confirm, setConfirm] = useState(null); // "enable" | "disable" | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errorIsConflict, setErrorIsConflict] = useState(false);
  const [setupBlockers, setSetupBlockers] = useState(null); // blockers returned by a refused Finish setup
  const [attention, setAttention] = useState(null); // { waiting, ended } | null

  const known = Boolean(status && typeof status === "object" && status.packing_slip && typeof status.packing_slip === "object");

  const loadAttention = useCallback(async () => {
    try {
      const res = await printingApi.packingSlipAttention();
      setAttention({ waiting: res?.waiting || [], ended: res?.ended || [] });
    } catch (e) {
      setAttention(null);
    }
  }, []);
  useEffect(() => { if (known) loadAttention(); }, [known, loadAttention]);

  const dismiss = async (orderId) => {
    try {
      const res = await printingApi.dismissPackingSlip(orderId);
      notify(res?.dismissed ? "success" : "info", res?.dismissed
        ? "The waiting packing slip was dismissed. It will not print automatically; manual printing is still available."
        : res?.reason === "already_sent_to_printer"
          ? "Too late to dismiss: this packing slip was already sent to the printer and may have printed."
          : "That packing slip was no longer waiting.");
      await loadAttention();
    } catch (e) {
      notify("error", operatorMessage(e, "Could not dismiss the packing slip. Nothing was changed."));
    }
  };
  const gateOn = known && status.master_gate_enabled === true;
  const slip = (known && status.packing_slip) || {};
  const active = slip.active === true;
  const effective = gateOn && active;
  const triggers = (slip.payment_triggers || []).map((t) => TRIGGER_LABELS[t] || t).join(", ");
  const managed = slip.managed_rules && typeof slip.managed_rules === "object" ? slip.managed_rules : null;
  const openRules = () => { if (onGoTo) onGoTo("rules"); };
  const destination = slip.printer_id
    ? "the printer selected in its settings"
    : `${ROLE_LABELS[slip.printer_role] || slip.printer_role || "Packing printer"} (falls back to a General printer, then the Customer receipt printer)`;

  const perform = async (action, { repair = false } = {}) => {
    setBusy(true); setError(null); setErrorIsConflict(false);
    try {
      const res = await printingApi.setAutomaticPackingSlips(action);
      setConfirm(null);
      const now = res?.automatic_printing?.packing_slip;
      if (repair) {
        notify("success", "Both managed packing-slip rules are in place. The activation time did not change.");
      } else if (action === "enable") {
        notify("success", now?.effective
          ? "Automatic packing slips are enabled. Orders paid from now on will print one packing slip."
          : "Automatic packing slips are enabled for this business, but the server master switch is off, so nothing prints yet.");
      } else {
        notify("success", "Automatic packing slips are disabled for this business.");
      }
      if (onChanged) await onChanged();
    } catch (e) {
      // The legacy-rule conflict says exactly what to do (disable an enabled
      // rule, delete a disabled one), so the server's own text is shown.
      const detail = e?.response?.data?.detail;
      const message = errorCode(e) === "automatic_packing_slip_legacy_rule_conflict" && typeof detail?.message === "string" && detail.message
        ? detail.message
        : operatorMessage(e, "Could not change automatic packing slips. Nothing was changed.");
      if (repair) notify("error", message);
      setError(message);
      setErrorIsConflict(errorCode(e) === "automatic_packing_slip_legacy_rule_conflict");
    } finally {
      setBusy(false);
    }
  };

  // Oct 2026 (Product Owner) — explicit Finish setup for a business still
  // on the rules the earlier version wrote. Never changes when packing
  // slips started and never prints; a refusal shows its exact causes.
  const finishSetup = async () => {
    setBusy(true); setSetupBlockers(null);
    try {
      const res = await printingApi.finishPackingSlipSetup();
      notify("success", res?.result === "already_managed"
        ? "The packing-slip rules were already secured as managed rules."
        : "Setup finished: both packing-slip rules are now managed by Automatic Packing Slips. When packing slips started did not change.");
      if (onChanged) await onChanged();
    } catch (e) {
      const blockers = e?.response?.data?.detail?.details?.blockers;
      if (Array.isArray(blockers) && blockers.length) setSetupBlockers(blockers);
      else notify("error", operatorMessage(e, "Finish setup did not complete. Nothing was changed."));
    } finally {
      setBusy(false);
    }
  };
  const setup = (managed && managed.finish_setup) || {};
  const shownBlockers = setupBlockers || (setup.needed && !setup.eligible ? setup.blockers || [] : []);

  const closeConfirm = () => { if (!busy) { setConfirm(null); setError(null); setErrorIsConflict(false); } };
  const conflictLink = (testId) => (onGoTo
    ? <button type="button" className={btnSecondary} onClick={() => { closeConfirm(); openRules(); }} data-testid={testId}>Open Rules &amp; Triggers</button>
    : null);

  return (
    <SectionCard
      title="Automatic packing slips"
      description="Prints one packing slip for every paid order when it is ready to be packed, alongside and independent of the customer receipt."
      testId="auto-packing-panel"
      actions={isAdmin && known ? (
        active
          ? <button type="button" className={btnDanger} onClick={() => setConfirm("disable")} disabled={busy} data-testid="auto-packing-disable">Disable automatic packing slips</button>
          : <button type="button" className={btnPrimary} onClick={() => setConfirm("enable")} disabled={busy} data-testid="auto-packing-enable">Enable automatic packing slips from now</button>
      ) : null}
    >
      {!known && <Notice tone="info" testId="auto-packing-unknown">The server did not report the automatic packing-slip status. Refresh to try again.</Notice>}
      {known && (
        <>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
              <dt className="text-xs uppercase tracking-wide text-stone-600">Server master switch</dt>
              <dd className="mt-1 flex flex-wrap items-center gap-2">
                <Pill tone={gateOn ? "success" : "neutral"} testId="auto-packing-master-gate">{gateOn ? "ON" : "OFF"}</Pill>
                <span className="text-xs text-stone-600">Shared with automatic receipts. Set by operations on the server.</span>
              </dd>
            </div>
            <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
              <dt className="text-xs uppercase tracking-wide text-stone-600">This business</dt>
              <dd className="mt-1 flex flex-wrap items-center gap-2">
                <Pill tone={active ? "success" : "neutral"} testId="auto-packing-tenant-status">{active ? "Active" : "Inactive"}</Pill>
                {active && slip.auto_print_since && (
                  <span className="text-xs text-stone-600" data-testid="auto-packing-since">Enabled {formatWhen(slip.auto_print_since)}. Only orders paid after that moment qualify.</span>
                )}
                {!active && <span className="text-xs text-stone-600">No packing slip prints automatically.</span>}
              </dd>
            </div>
          </dl>
          <ul className="list-disc space-y-1 pl-5 text-xs text-stone-700" data-testid="auto-packing-when">
            <li>Dine-In, Takeout, Pickup and Parcel Shipping: as soon as the payment is confirmed (after your acceptance for orders you review first).</li>
            <li>Own Delivery and Uber Direct: only after you accept the order and, for Uber Direct, the courier request succeeds. A delivery that is refunded or held for review prints nothing.</li>
            <li>Scheduled orders: when the order is activated for the kitchen, not when it was paid — however far ahead it was booked.</li>
            <li>Waiting is limited: an order not accepted within 24 hours of payment, a scheduled order not activated within 24 hours of its scheduled time, or a slip with no printer for 24 hours stops waiting. A completed, cancelled or refunded order stops it at once.</li>
            <li>Delivery and shipping slips include the recipient, address and drop-off instructions. Phone numbers and payment details never print.</li>
          </ul>
          {active && (
            <p className="text-xs text-stone-600" data-testid="auto-packing-details">
              Prints on: {triggers || "none"} · Copies: {slip.copy_count || 1} · Printer: {destination}. Change these under Defaults and catalog overrides → Packing slip.
            </p>
          )}
          <Notice tone={effective ? "success" : "warning"} testId="auto-packing-effective" role="status">
            {effective && "Automatic packing slips are live for this business: each paid order prints one packing slip when it is ready to be packed."}
            {!effective && active && !gateOn && "This business is enabled, but the server master switch is OFF, so nothing prints automatically."}
            {!effective && !active && "Automatic packing slips are off for this business. Customer receipts are not affected."}
          </Notice>
          {managed && managed.state === "conflict" && (
            <Notice tone="danger" testId="auto-packing-rules-conflict">
              Another enabled packing-slip rule exists besides the two rules managed here, so an order could print more than one packing slip.
              Disable it under Rules &amp; Triggers. {conflictLink("auto-packing-open-rules")}
            </Notice>
          )}
          {managed && managed.state === "needs_setup" && setup.needed && (
            <Notice tone="warning" testId="auto-packing-needs-setup">
              <strong className="block">Automatic packing slips are active, but the rules need to be secured as managed rules.</strong>
              Finish setup marks the existing Cash received and Payment paid/captured rules as managed. It does not change when packing slips started and does not print anything.
              {isAdmin && setup.eligible && !setupBlockers && (
                <> <button type="button" className={btnSecondary} onClick={finishSetup} disabled={busy} data-testid="auto-packing-finish-setup">{busy ? "Securing…" : "Finish setup"}</button></>
              )}
            </Notice>
          )}
          {shownBlockers.length > 0 && (
            <Notice tone="danger" testId="auto-packing-setup-blocked" title="Finish setup cannot proceed. Nothing was changed; review these first:">
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {shownBlockers.map((b, i) => <li key={`${b.code}-${i}`} data-testid={`auto-packing-setup-blocker-${b.code}`}>{b.message}</li>)}
              </ul>
              {conflictLink("auto-packing-setup-open-rules")}
            </Notice>
          )}
          {managed && managed.state === "needs_setup" && !setup.needed && active && (
            <Notice tone="warning" testId="auto-packing-managed-missing">
              One of the two managed packing-slip rules is missing. Restoring it does not print anything and keeps the activation time.
              {isAdmin && <> <button type="button" className={btnSecondary} onClick={() => perform("enable", { repair: true })} disabled={busy} data-testid="auto-packing-restore-rules">{busy ? "Restoring…" : "Restore managed rules"}</button></>}
            </Notice>
          )}
          {attention && (attention.waiting.length > 0 || attention.ended.length > 0) && (
            <div className="space-y-2" data-testid="auto-packing-attention">
              <p className="text-xs font-medium uppercase tracking-wide text-stone-600">Packing slips not printed automatically</p>
              <ul className="divide-y divide-stone-200 rounded-md border border-stone-200 text-sm">
                {attention.waiting.map((w) => (
                  <li key={`w-${w.order_id}`} className="flex flex-wrap items-center justify-between gap-2 p-2" data-testid={`auto-packing-waiting-${w.order_id}`}>
                    <span><strong>{w.order_code || w.order_id}</strong> · {slipReasonLabel(w.reason)}</span>
                    {isAdmin && <button type="button" className={btnSecondary} onClick={() => dismiss(w.order_id)} data-testid={`auto-packing-dismiss-${w.order_id}`}>Dismiss</button>}
                  </li>
                ))}
                {attention.ended.map((e, i) => (
                  <li key={`e-${e.order_id}-${i}`} className="p-2 text-stone-700" data-testid={`auto-packing-ended-${e.order_id}`}>
                    <strong>{e.order_code || e.order_id}</strong> · {slipReasonLabel(e.reason)}{e.at ? ` · ${formatWhen(e.at)}` : ""}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-stone-600">Completed, cancelled or refunded orders never print late. Staff can still print a packing slip by hand from the order.</p>
            </div>
          )}
          {!isAdmin && <p className="text-xs text-stone-600" data-testid="auto-packing-read-only">Only a business administrator can enable or disable automatic packing slips.</p>}
        </>
      )}

      <Modal
        open={confirm === "enable"}
        title="Enable automatic packing slips from now?"
        description="The activation time is set by the server clock at the moment you confirm. Orders paid before that moment never print a packing slip automatically; there is no backfill."
        onClose={closeConfirm}
        testId="auto-packing-confirm-enable"
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-stone-800">
          <li>Applies only to this business.</li>
          <li>Turns on its two managed printing rules — cash received and card payment. Only the rule matching the order's payment method fires, so each order prints one packing slip.</li>
          <li>Prints one packing slip per paid order to the Packing printer; if there is none, a General printer, then the Customer receipt printer. Never a kitchen printer. If no printer is available the slip waits up to 24 hours for one.</li>
          <li>Copies, printer and layout keep their current packing-slip settings (default: 1 copy, default layout).</li>
          <li>{gateOn ? "The server master switch is ON, so printing starts with the next confirmed payment." : "The server master switch is OFF, so nothing prints until operations turn it on."}</li>
          <li>Enabling does not print anything by itself.</li>
        </ul>
        {error && <Notice tone="danger" testId="auto-packing-error">{error} {errorIsConflict && conflictLink("auto-packing-error-open-rules")}</Notice>}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={closeConfirm} disabled={busy} data-testid="auto-packing-cancel">Cancel</button>
          <button type="button" className={btnPrimary} onClick={() => perform("enable")} disabled={busy} data-testid="auto-packing-confirm">{busy ? "Enabling…" : "Enable from now"}</button>
        </div>
      </Modal>

      <Modal
        open={confirm === "disable"}
        title="Disable automatic packing slips?"
        description="Clears this business's packing-slip activation time and turns off its two managed packing-slip rules. No order in any catalog prints a packing slip automatically after this. Manual packing slips, customer receipts, catalog settings and jobs already queued are not affected."
        onClose={closeConfirm}
        testId="auto-packing-confirm-disable"
      >
        {error && <Notice tone="danger" testId="auto-packing-error">{error}</Notice>}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={closeConfirm} disabled={busy} data-testid="auto-packing-cancel">Cancel</button>
          <button type="button" className={btnDanger} onClick={() => perform("disable")} disabled={busy} data-testid="auto-packing-confirm">{busy ? "Disabling…" : "Disable"}</button>
        </div>
      </Modal>
    </SectionCard>
  );
}
